import React, { createContext, useContext, useMemo } from 'react';

import { schema } from '../schema';
import { useStaticProps } from './StaticPropsContext';
import { queryParameterContext } from './QueryParameterContext';
import { Program, usePrograms, ProgramsSearchQuery } from './../data/usePrograms';

export interface SearchResult {
  programs: Array<Program>;
  totalPrograms: number;

  isPreviousProgramsData: boolean;
  isLoadingPrograms: boolean;
  isFetchingPrograms: boolean;
  isProgramsError: boolean;
}

const searchResultContext = createContext<SearchResult | undefined>(undefined);

export const SearchResultContextProvider = ({ children }: { children: React.ReactNode }) => {
  const staticProps = useStaticProps();
  const { parameters } = queryParameterContext.useQueryParameter();

  const searchQueryParams = useMemo(() => {
    const params = {
      'page[number]': 1,
      'page[size]': 20,
      'filter[q]': '',
    } as ProgramsSearchQuery;
    schema.parameters.forEach((parameter) => {
      const value = parameter.getValue({ staticProps, parameters });
      const defaultValue = parameter.getDefaultValue({ defaultParameters: staticProps.defaultParameters });
      const result = parameter.toSearchQuery({
        value,
        staticProps,
        defaultValue,
        parameters,
      });
      if (!result) return;
      Object.assign(params, result);
    });
    return params;
  }, [staticProps, parameters]);

  const { programs, totalPrograms, isLoadingPrograms, isFetchingPrograms, isPreviousProgramsData, programsError } =
    usePrograms(searchQueryParams);

  const value = useMemo(
    () => ({
      programs,
      totalPrograms,
      isPreviousProgramsData,
      isLoadingPrograms,
      isFetchingPrograms,
      isProgramsError: !!programsError,
    }),
    [programs, isPreviousProgramsData, totalPrograms, isLoadingPrograms, isFetchingPrograms, programsError]
  );

  return <searchResultContext.Provider value={value}>{children}</searchResultContext.Provider>;
};

export const useSearchResult = () => {
  const value = useContext(searchResultContext);
  if (value === undefined) {
    throw new Error('useSearchResult must be used within a SearchResultContextProvider');
  }
  return value;
};
